import { useEffect, useMemo, useRef, useState } from 'react'
import { inject, observer } from 'mobx-react'
import PropTypes from 'prop-types'

import { SVGWrapper } from '../../../components'
import { IconClose } from '../../../assets/icons'
import heyNicWordmark from '../../../assets/images/heyNicWordmark.png'
import ChatView from '../../hey-nic/ChatView'
import ChatInput from '../../hey-nic/ChatInput'
import ChatDisclaimer from '../../hey-nic/ChatDisclaimer'
import { turnModifiedCase } from './caseRefresh'
import { stepChatDefaults, stepChatTitle } from './stepChatDefaults'
import {
  ChatShell,
  ChatHeader,
  HeaderWordmark,
  HeaderCloseButton,
  ChatBody,
  ChatFooter,
} from './styled'

/*
 * The chatbot itself, shared by the desktop side panel and the mobile overlay.
 *
 * Its transcript is the main chat's (`ChatView`) in `compact` mode — the narrow
 * column borrows the mobile bubble layout, which is what the design does. Around
 * it sit a fixed header and a fixed footer, so only the transcript scrolls.
 *
 * The opening comment and its pills come from the front end (see
 * stepChatDefaults) and stay client-side: the agent is not called until the user
 * taps a pill, and nothing is persisted until then either. Outside the summary
 * step the pills are the only way in — there is no input field.
 */
const AdviceStepChat = ({ store, stepPath, stepLabel, showInput, onClose, onCaseRefresh }) => {
  const [draft, setDraft] = useState('')
  const [scrolled, setScrolled] = useState(false)
  const inputRef = useRef(null)
  const streamingRef = useRef(store.isAdviceStepStreaming(stepPath))

  const { comment, suggestions } = stepChatDefaults()
  // This step's own chat — never whatever happens to be in the store's shared
  // `activeChat` slot (see Chat.adviceChatFor).
  const chat = store.adviceChatFor(stepPath)
  // Any stream blocks sending (there is one at a time), but only THIS step's
  // stream is this panel's own response.
  const streaming = store.responseLoading
  const streamingThisStep = store.isAdviceStepStreaming(stepPath)

  // Timestamp the opening comment when the step is entered, so it doesn't tick
  // over on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const openedAt = useMemo(() => new Date().toISOString(), [stepPath])

  /*
   * Load this step's saved chat, if it has one.
   *
   * `streaming` is a dependency because the store declines to load anything while
   * a stream it doesn't own is running — that would abort the reply. Re-running
   * when the flag clears is what picks the transcript up afterwards.
   */
  useEffect(() => {
    store.openAdviceStepChat(stepPath).catch(err => {
      // The step keeps its opening comment and pills; only the saved transcript
      // is missing, and the next pill tap starts a fresh turn.
      console.error('[AdviceStepChat] failed to open the step chat:', err)
    })
  }, [store, stepPath, streaming])

  /*
   * A finished turn may have changed the case behind the step (e.g. the user
   * asked to change their retirement age), so hand off to the flow's refresh.
   * Keyed off the streaming flag falling rather than the message list, so it runs
   * once per turn — and off THIS step's flag, so the standalone chat finishing a
   * reply in the background can't trigger a refresh of the advice case.
   */
  useEffect(() => {
    const wasStreaming = streamingRef.current
    streamingRef.current = streamingThisStep
    if (!wasStreaming || streamingThisStep) return
    if (turnModifiedCase(store.adviceChatFor(stepPath)?.lastMessage)) onCaseRefresh?.()
  }, [streamingThisStep, store, stepPath, onCaseRefresh])

  const send = text => {
    const trimmed = text.trim()
    if (!trimmed || streaming) return
    store
      .sendAdviceMessage(stepPath, trimmed, stepChatTitle(stepLabel))
      .catch(err => console.error('[AdviceStepChat] failed to send:', err))
  }

  return (
    <ChatShell>
      <ChatHeader scrolled={scrolled}>
        <HeaderWordmark src={heyNicWordmark} alt='Hey Nic' />
        <HeaderCloseButton type='button' onClick={onClose} aria-label='Close Hey Nic'>
          <SVGWrapper svg={IconClose} size='mediumLarge' />
        </HeaderCloseButton>
      </ChatHeader>

      <ChatBody>
        <ChatView
          compact
          flat
          hideEmptyState
          chat={chat}
          leadingTurns={[{ role: 'bot', text: comment, timestamp: openedAt, suggestions }]}
          pillsDisabled={streaming}
          onSuggestion={send}
          onScrolledChange={setScrolled}
        />
      </ChatBody>

      {/* Without the input there is no pill for the transcript to disappear behind,
          so the footer carries a fill and hides the content itself. */}
      <ChatFooter solid={!showInput}>
        {showInput ? (
          <ChatInput
            compact
            autoFocus={false}
            chat={chat}
            text={draft}
            setText={setDraft}
            inputRef={inputRef}
            onSubmit={send}
          />
        ) : (
          <ChatDisclaimer />
        )}
      </ChatFooter>
    </ChatShell>
  )
}

AdviceStepChat.propTypes = {
  /** The SBSA step path, which keys both the copy and the saved transcript. */
  stepPath: PropTypes.string.isRequired,
  /** Step label, used to name the chat in the main Hey Nic Recents list. */
  stepLabel: PropTypes.string,
  /** Show the free-text field (summary step only). */
  showInput: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  /** Called after a turn that changed the user's case. */
  onCaseRefresh: PropTypes.func,
}

export default inject('store')(observer(AdviceStepChat))
